class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.onOverlayClick = this.close.bind(this);
    this.onEscape = (evt) => evt.code === 'Escape' && this.close();

    // basic keyboard / overlay handlers
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.onOverlayClick);

    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    // Listen for the AJAX result to update the drawer.
    // Expect event.detail to be the parsed JSON state the server returned.
    this._onCartChanged = (event) => {
      const parsedState = event?.detail;
      if (!parsedState) return;
      this.renderContents(parsedState);
    };

    // Common event names: 'cart:changed' — dispatch from your AJAX success handler:
    // window.dispatchEvent(new CustomEvent('cart:changed', { detail: parsedState }));
    window.addEventListener('cart:changed', this._onCartChanged);
    window.addEventListener('cart:clear', this._onCartChanged); // optional alias
  }

  disconnectedCallback() {
    window.removeEventListener('cart:changed', this._onCartChanged);
    window.removeEventListener('cart:clear', this._onCartChanged);
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.removeEventListener('click', this.onOverlayClick);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // use requestAnimationFrame to reliably trigger CSS animation
    requestAnimationFrame(() => {
      this.classList.add('animate');
      // second frame to ensure transition runs
      requestAnimationFrame(() => this.classList.add('active'));
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    // optionally remove animate after transition ends (cleanup)
    const onTransitionEnd = () => {
      this.classList.remove('animate');
      this.removeEventListener('transitionend', onTransitionEnd);
    };
    this.addEventListener('transitionend', onTransitionEnd, { once: true });

    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      const expanded = event.currentTarget.closest('details').hasAttribute('open');
      event.currentTarget.setAttribute('aria-expanded', String(!expanded));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // remove empty class if necessary
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');

    this.productId = parsedState.id;

    // Render each section returned by your cart AJAX payload
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const html = parsedState.sections?.[section.id] ?? parsedState.sections?.[section.section] ?? '';
      if (!html) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });

    // ensure overlay handler is present (avoid adding duplicates)
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.removeEventListener('click', this.onOverlayClick);
      overlay.addEventListener('click', this.onOverlayClick);
    }

    // open drawer to show results; use rAF so animation fires reliably
    requestAnimationFrame(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector);
    return node ? node.innerHTML : html;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
